import styled, { css } from 'styled-components';

import type { TypographyProps } from '@/components/Typography';
import { Typography } from '@/components/Typography';
import type { Space } from '@/theme/types';

import type { TabSize, TabVariant } from './Tab';

const PX: { [key in TabSize]: keyof Space } = {
  s: 's075',
  m: 's100',
  l: 's125',
};

export const switchPaddingSettings = (
  size: TabSize,
): Pick<TypographyProps, 'px' | 'py'> => {
  switch (size) {
    case 's':
      return { px: PX.s, py: 's050' };
    case 'm':
      return { px: PX.m, py: 's075' };
    case 'l':
      return { px: PX.l, py: 's100' };
    default:
      return { px: 's000', py: 's000' };
  }
};

const underlineStyles = css<{ isActive: boolean; size: TabSize }>`
  color: ${(props) => props.theme.colors.default};
  background-color: transparent;

  &:hover,
  &:focus {
    background-color: ${(props) => props.theme.colors.defaultAlpha4};
  }
  &:active {
    background-color: ${(props) => props.theme.colors.defaultAlpha8};
  }

  &:before {
    position: absolute;
    content: '';
    display: block;
    background-color: ${(props) => props.theme.colors.default};
    height: ${(props) => props.theme.components.tab.bottomUnderlineWidth}px;
    width: calc(100% - 2 * ${(props) => props.theme.space[PX[props.size]]});
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
  }

  ${(props) =>
    props.isActive &&
    css`
      &:before {
        opacity: 1;
      }
    `}
`;

const segmentedStyles = css<{ isActive: boolean }>`
  ${({ isActive, theme }) => css`
    transition:
      background-color 0.15s ease-in-out,
      color 0.15s ease-in-out;
    flex: 1;
    justify-content: center;
    text-align: center;
    text-transform: uppercase;
    font-weight: ${theme.fontWeights.bold};
    background-color: ${isActive
      ? theme.components.tab.segmented.activeBackground
      : theme.components.tab.segmented.background};
    color: ${isActive
      ? theme.components.tab.segmented.activeColor
      : theme.components.tab.segmented.color};
  `}
`;

export const Wrapper = styled(Typography)<{
  isActive: boolean;
  size: TabSize;
  variant: TabVariant;
}>`
  display: inline-flex;
  position: relative;
  text-decoration: none;
  transition: background-color 0.15s ease-in-out;

  ${(props) =>
    props.variant === 'segmented' ? segmentedStyles : underlineStyles}
`;
